import { apm } from '@elastic/apm-rum';
import { getGlobalSettings } from 'common/global-settings';

/**
 * Messages of errors that are triggered by browser extensions / IndexedDB quirks
 * and do not originate from fileee code. Do not report them.
 */
const IGNORED_ERROR_MESSAGES = [
  'A mutation operation was attempted on a database that did not allow mutations',
  'Transaction timed out due to inactivity.'
];

export function setupApm() {
  apm.init({
    serviceName: 'fileee-webapp',
    // Same-origin RUM intake: /api/apm is routed to the APM server by the
    // cluster gateway (argocd: httproute-apm-rum.yaml), so no CORS and errors
    // never leave the app's own domain. The agent appends
    // /intake/v2/rum/events itself.
    serverUrl: `${window.location.origin}/api/apm`,
    serviceVersion: __GIT_REVISION__,
    environment: getEnvironmentForApm(),
    active: shouldSendCrashReports(),
    // Errors are always reported. Performance transactions are disabled for now
    // to keep Elasticsearch ingest volume low (FILE-2413). Raise carefully.
    transactionSampleRate: 0,
    centralConfig: false
  });

  apm.addFilter((payload) => {
    if (payload.errors) {
      payload.errors = payload.errors.filter(
        (error) =>
          !IGNORED_ERROR_MESSAGES.some((ignored) =>
            error?.exception?.message?.includes(ignored)
          )
      );
      if (payload.errors.length === 0 && !payload.transactions) {
        return false;
      }
      payload.errors.forEach(rewriteStacktraceUrls);
    }
    return payload;
  });

  if (getGlobalSettings().userId) {
    setApmUserId(getGlobalSettings().userId);
  }

  // Verification hook (FILE-2413): run window.__fileeeApmTestError() in the
  // browser console to throw an error FROM BUNDLE CODE. Console-thrown errors
  // have no source file ("inline script") and can never be symbolicated, so
  // they cannot verify sourcemaps. The setTimeout is required: exceptions from
  // console-invoked calls don't reach window.onerror, async ones do.
  (window as any).__fileeeApmTestError = () => {
    setTimeout(() => {
      throw new Error('APM symbolication test error');
    }, 0);
  };
}

/**
 * Attach the fileee user id (id only — no email/username) to all APM events,
 * so errors are filterable by user.id in Kibana.
 */
export function setApmUserId(userId: string) {
  apm.setUserContext({ id: userId });
}

function getEnvironmentForApm() {
  const runTimeEnv = getGlobalSettings().runtimeEnv;

  if (
    runTimeEnv === 'live' ||
    runTimeEnv === 'release_candidate' ||
    runTimeEnv === 'staging' ||
    runTimeEnv === 'test'
  ) {
    return runTimeEnv;
  }
  return 'unknown';
}

function shouldSendCrashReports() {
  if (__DEV__) return false;
  if (BUILD_VAR_IS_EMBEDDED_APP) return false;
  return true;
}

/**
 * The webapp is served from multiple domains (my.fileee.com, my.workeee.de,
 * white-label hosts). Sourcemaps are uploaded to Kibana once per build with
 * bundle_filepath prefixed by 'http://dynamichost'. Rewrite stack frame URLs
 * accordingly so Kibana can match them (same trick as the former Rollbar
 * transformTrace).
 */
const DynamicHostName = 'http://dynamichost';

function rewriteStacktraceUrls(error) {
  try {
    const frames = error?.exception?.stacktrace;
    if (!frames) return;
    for (const frame of frames) {
      if (frame.abs_path) {
        frame.abs_path = frame.abs_path.replace(window.location.origin, DynamicHostName);
      }
      if (frame.filename) {
        frame.filename = frame.filename.replace(window.location.origin, DynamicHostName);
      }
    }
  } catch (e) {
    console.error('Failed to rewrite stacktrace URLs', e);
  }
}
